import { X, Users } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAddTeamCredits } from '@/hooks/useTeams';

interface TeamCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
  userId?: number;
}

export default function TeamCreditsModal({
  isOpen,
  onClose,
  teamId,
  teamName,
  userId,
}: TeamCreditsModalProps) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const addCredits = useAddTeamCredits();
  const toast = useToast();

  if (!isOpen) return null;

  const handleClose = () => {
    setAmount('');
    setDescription('');
    onClose();
  };

  const handleSubmit = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents <= 0) {
      toast.error('Invalid amount', 'Enter a positive amount');
      return;
    }
    try {
      const res = await addCredits.mutateAsync({
        teamId,
        amount_cents: cents,
        description: description || undefined,
        userId,
      });
      toast.success('Team credit added', res.message);
      handleClose();
    } catch (e) {
      toast.error(
        'Failed to add credit',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Add credit to team {teamName}
              </CardTitle>
              <CardDescription>
                Credit goes to the team's Stripe customer, not to this user
                personally.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Amount (USD)
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="50.00"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Reason for credit"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Credit applies automatically to {teamName}'s next invoice.
          </p>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={addCredits.isPending || !amount}
              className="flex-1"
            >
              {addCredits.isPending ? 'Adding...' : 'Add credit'}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
